import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { formatHours, weekNeedsAttention } from '@time-tracking/shared';
import type {
  AdminCategoryItem,
  LineRestrictedReason,
  TimesheetLineItem,
  TimesheetResponse,
  WeekStatusResponse,
} from '@time-tracking/shared';

import { ToastRegion } from '../components/ToastRegion.js';
import { useToasts } from '../components/useToasts.js';
import {
  ApiError,
  useAdminCategories,
  useMe,
  useOutstandingWeeks,
  usePatchLineDescription,
  useRecallTimesheet,
  useSubmitTimesheet,
  useTimesheet,
  useUpdateEntry,
  useWeekStatus,
} from '../api/hooks.js';
import { Layout } from '../components/Layout.js';
import { NotePopover } from '../components/NotePopover.js';
import { PeriodBar, WEEK_RANGE_HEADING_ID } from '../components/PeriodBar.js';
import { apiStatusToUi, type UiStatus } from '../components/PeriodStatusPill.js';
import { ReturnedBanner } from '../components/ReturnedBanner.js';
import { SubmitDialog } from '../components/SubmitDialog.js';
import { LockIcon } from '../components/icons/LockIcon.js';
import { WarningIcon } from '../components/icons/WarningIcon.js';
import { isReportingPeriodClosedError } from '../api/client.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { computeRecap } from '../utils/recap.js';
import { categoryTypeLabel } from '../utils/categoryType.js';
import { taskRowClassName } from '../utils/taskRow.js';
import {
  addWeeks,
  formatWeekRange,
  getMondayOf,
  isIsoDate,
  todayMondayIso,
  weekEndOf,
} from '../utils/weekDates.js';

// ---- Types ----

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

// ---- Restricted-row copy ----

// A row goes restricted when its project is archived, its task is archived, the
// contributor no longer holds the assignment, or — for a non-project row — its
// category went inactive, passed its lock cutoff, or became approvers-only.
// Different things happened to the contributor's week, so the badge says which:
// a bare "Inactive" would tell them a row changed without telling them why,
// which is the whole point of the signal. Keyed by the shared union so a new
// reason cannot ship without copy.
//
// Both sections read these maps. The non-project section used to derive
// `isInactive` / `isCatLocked` from its own copy of the category catalog and
// render its own badges; that branch is gone, because `approver_only` cannot be
// derived client-side at all (it needs the catalog AND `me.role`, two
// independently-stale caches) and keeping half the reasons client-derived would
// have left the grid dimming a row for one reason while the API refused it for
// another.
const RESTRICTED_BADGE_LABEL: Record<LineRestrictedReason, string> = {
  project_archived: 'Project archived',
  task_archived: 'Task archived',
  unassigned: 'Not assigned',
  category_inactive: 'Inactive',
  category_locked: 'Locked',
  // Kept short to match the admin pane's pill. The longer-form copy below is
  // where the accurate rule lives — administrators keep these rows too.
  approver_only: 'Approvers only',
};

// Appended to the hours input's accessible name. The badge lives in the row's
// first cell and a grid cursor can land straight on the input, so the reason has
// to be audible at the point of interaction too.
const RESTRICTED_INPUT_NOTE: Record<LineRestrictedReason, string> = {
  project_archived: 'project archived',
  task_archived: 'task archived',
  unassigned: 'no longer assigned to this task',
  category_inactive: 'inactive category',
  category_locked: 'locked for this period',
  // "and administrators" rather than the badge's bare "Approvers only": the
  // badge keeps the prototype's short pill label, and the longer-form copy is
  // where the accurate rule lives. Administrators hold these rows too.
  approver_only: 'for approvers and administrators only',
};

// Added to the note only while the sheet is still editable, so it states what is
// still possible rather than only what is not. Kept separate because a restricted
// row on a SUBMITTED (or period-closed) sheet is read-only for a different
// reason, and promising a lowering that cannot happen there would be a lie.
const RESTRICTED_RATCHET_NOTE = 'hours can be lowered but not increased';

// Says 'not raise them again' because lowering is genuinely one-way: clearing to
// zero deletes the time_entries row, and that row is where the ratchet reads its
// ceiling from, so it drops to 0 with no way back. Copy that implied clearing was
// safely reversible was inviting contributors into a trap.
const RESTRICTED_TAIL =
  'You can still lower the hours you already logged, but not raise them again afterwards.';

// The names a restricted row can be called by. Shaped like the wire DTO rather
// than like one kind of row: a project row has a project and a task and no
// category, a non-project row has a category and neither of the others, and
// each reason quotes the one thing that actually closed the row. The earlier
// `{projectName, taskName}`-only signature would have forced the three category
// entries to take names a category line does not have.
interface RestrictedRowNames {
  /** Null on a non-project row. */
  projectName: string | null;
  /** Null on a non-project row. */
  taskName: string | null;
  /** Null on a project row. */
  categoryName: string | null;
}

// The client half of the server's ratchet, worded to match it. `project_archived`
// and `unassigned` are verbatim the 409 bodies from assertAssignmentOpenForEntry
// (packages/api/src/repositories/timesheets.ts) so a refusal here and a refusal
// there read identically. The server has no separate archived-task message — an
// archived task closes its assignments, and activeFlag alone cannot say which
// archive did it — so `task_archived` is the client's own, in the same shape.
const RESTRICTED_MESSAGE: Record<LineRestrictedReason, (names: RestrictedRowNames) => string> = {
  project_archived: ({ projectName }) =>
    `“${projectName ?? 'That project'}” is archived and can’t receive new hours. ${RESTRICTED_TAIL}`,
  task_archived: ({ taskName }) =>
    `“${taskName ?? 'That task'}” is archived and can’t receive new hours. ${RESTRICTED_TAIL}`,
  unassigned: () =>
    `You are no longer assigned to this task, so it can’t receive new hours. ${RESTRICTED_TAIL}`,
  // Inactive and locked are WALLS server-side — assertCategoryOpenForEntry
  // refuses every new value, including a decrease — so their copy deliberately
  // omits RESTRICTED_TAIL. Promising a lowering the API would reject would be
  // the same lie in the other direction as disabling the approver-only row.
  category_inactive: ({ categoryName }) =>
    `“${categoryName ?? 'That category'}” is no longer active, so it can’t receive new hours.`,
  category_locked: ({ categoryName }) =>
    `“${categoryName ?? 'That category'}” is locked for this period, so it can’t receive new hours.`,
  // ONE variant, not two, and that is a limitation of the payload rather than a
  // choice. Two different things produce this row — an approver demoted by an
  // ordinary team edit, and a contributor whose in-use category was flipped to
  // approvers-only — and the second is the commoner one at rollout. The server
  // sends the same `approver_only` for both, `me.role` reads `contributor` in
  // both (a demoted user IS a contributor), and `AdminCategoryItem` carries no
  // flip timestamp, so nothing in what the client is given tells them apart.
  // Rather than guess, this names both possibilities: whichever happened, the
  // reader is told the true reason and what they can still do. Splitting the
  // copy needs a server-side signal — a distinct reason member, or a cause on
  // the line — not a client-side inference.
  approver_only: ({ categoryName }) =>
    `“${categoryName ?? 'That category'}” now goes to approvers and administrators only — either the category changed or your role did, so it can’t receive new hours. ${RESTRICTED_TAIL}`,
};

// Said in words as well as in the `readonly` attribute. The attribute is the
// programmatic truth and NVDA/JAWS speak it, but VoiceOver's handling of
// `readonly` (as distinct from `disabled`) on a text input has a long history of
// being unreliable, and this note costs one clause. Not redundant ARIA — no
// `aria-readonly` is added, because the native attribute already carries the
// state for anything that maps it correctly.
const RESTRICTED_READ_ONLY_NOTE = 'read-only';

// The phrase appended to the hours input's accessible name. Three states, and
// the middle one is the reason this is a function rather than a lookup: a row
// clamped only by a 409 we have not refetched past yet has a real ceiling but no
// reason the server has told us, so it names the ceiling and invents nothing.
// The ratchet clause is added only while the sheet is editable — on a submitted
// sheet, or on a category wall, the row is read-only for a different reason, and
// promising a lowering that cannot happen would be a lie. That case says
// "read-only" instead, which is the truth there.
function restrictionNoteFor(
  restriction: LineRestrictedReason | null,
  pendingClamp: boolean,
  readOnly: boolean,
): string | undefined {
  if (restriction === null) {
    return pendingClamp && !readOnly ? RESTRICTED_RATCHET_NOTE : undefined;
  }
  return readOnly
    ? `${RESTRICTED_INPUT_NOTE[restriction]}, ${RESTRICTED_READ_ONLY_NOTE}`
    : `${RESTRICTED_INPUT_NOTE[restriction]}, ${RESTRICTED_RATCHET_NOTE}`;
}

// Why this line stopped accepting new hours, or null while it still does.
// `assignmentActive` and `restrictedReason` are derived together server-side, so
// the fallback is unreachable in practice — it exists so a closed row can never
// render as open just because the reason went missing.
//
// The fallback has to match the KIND of row, which is why it is not a bare
// `?? 'unassigned'`: a non-project row has no assignment to be unassigned from,
// so branding one "Not assigned" would explain it with something that cannot
// have happened. `category_inactive` is the category fallback because it is the
// first of the three in the server's own precedence and the most restrictive of
// them (a wall), so an unexplained closed row fails safe — closed and
// read-only — rather than offering hours the API would refuse.
function restrictionOf(line: TimesheetLineItem): LineRestrictedReason | null {
  if (line.assignmentActive) return null;
  if (line.restrictedReason !== null) return line.restrictedReason;
  return line.adminCategoryId !== null ? 'category_inactive' : 'unassigned';
}

// The three category reasons, named as a type so the row treatment below can be
// decided by role rather than by re-testing string literals at each use. Walls
// stop the row dead; the ratchet keeps it interactive. See
// assertCategoryOpenForEntry in packages/api/src/repositories/timesheets.ts —
// this must stay in lockstep with it.
type CategoryWallReason = Extract<LineRestrictedReason, 'category_inactive' | 'category_locked'>;

// A wall: the server refuses EVERY new value, so the input is genuinely
// read-only. `approver_only` is deliberately absent — it is a ratchet, and
// wiring it to `readOnly` would make the one correction the API still
// guarantees (lowering) unreachable through the only UI there is.
function isCategoryWall(
  restriction: LineRestrictedReason | null,
): restriction is CategoryWallReason {
  return restriction === 'category_inactive' || restriction === 'category_locked';
}

// ---- Pure helpers ----

// Decide whether the landing should open *last* week rather than the current
// one. A thin adapter from the week-status probe onto `weekNeedsAttention` in
// @time-tracking/shared, which is the single definition of "this week still
// needs action" — the same predicate the server-side outstanding-weeks scan
// uses, so the landing redirect and the nudge can never disagree. See that
// function for the rule itself.
function needsLastWeekAttention(week: WeekStatusResponse): boolean {
  return weekNeedsAttention({
    status: week.status,
    hasActiveAssignments: week.hasActiveAssignments,
  });
}

// ---- Attention-pulse session record ----

// ---- Pure helpers (continued) ----

function parseHours(raw: string): { minutes: number; error: string | null } {
  const s = raw.trim();
  if (!s) return { minutes: 0, error: null };

  // h:mm
  const hhmm = /^(\d+):([0-5]\d)$/.exec(s);
  if (hhmm) {
    const h = parseInt(hhmm[1] ?? '0', 10);
    const m = parseInt(hhmm[2] ?? '0', 10);
    const total = h * 60 + m;
    return total > 10080 ? { minutes: 0, error: 'Max 168h' } : { minutes: total, error: null };
  }

  // 1h30m / 2h / 30m
  const hm = /^(?:(\d+)h)?(?:(\d+)m)?$/.exec(s);
  if (hm && (hm[1] !== undefined || hm[2] !== undefined)) {
    const h = parseInt(hm[1] ?? '0', 10);
    const m = parseInt(hm[2] ?? '0', 10);
    const total = h * 60 + m;
    return total > 10080 ? { minutes: 0, error: 'Max 168h' } : { minutes: total, error: null };
  }

  // Decimal hours
  const n = parseFloat(s);
  if (isNaN(n) || n < 0) return { minutes: 0, error: 'Invalid' };
  const total = Math.round(n * 60);
  return total > 10080 ? { minutes: 0, error: 'Max 168h' } : { minutes: total, error: null };
}

/** Hour-cell input text: an empty cell means zero, so 0 renders blank. */
function cellText(minutes: number): string {
  return minutes === 0 ? '' : formatHours(minutes);
}

// ---- HourCell ----

interface HourCellProps {
  lineId: string;
  totalMinutes: number;
  readOnly: boolean;
  taskLabel: string;
  // A ratchet, not a wall. When set, the cell accepts any value at or below
  // `maxMinutes` and refuses only an INCREASE — the client half of the server
  // guard in assertAssignmentOpenForEntry. Deliberately not `readOnly`: an
  // approver who returns a sheet asking for 40h to be cut to 20h has to be
  // obeyable, and a disabled input makes the one correction the server still
  // guarantees unreachable through the only UI there is.
  maxMinutes?: number | undefined;
  // Why the ratchet is on, in the same words the server's 409 would use. Shown
  // as an inline alert while the cell has focus, and handed to `onReject` for a
  // toast that survives blur.
  clampMessage?: string | undefined;
  // Appended to the accessible name so the reason is audible where the
  // interaction happens, not only in the row's badge.
  restrictionNote?: string | undefined;
  onReject?: ((message: string) => void) | undefined;
  onCommit: (lineId: string, minutes: number) => void;
  registerRef: (lineId: string, el: HTMLInputElement | null) => void;
  onNavigate: (lineId: string, direction: 1 | -1) => void;
}

function HourCell({
  lineId,
  totalMinutes,
  readOnly,
  taskLabel,
  maxMinutes,
  clampMessage,
  restrictionNote,
  onReject,
  onCommit,
  registerRef,
  onNavigate,
}: HourCellProps) {
  const [value, setValue] = useState(() => cellText(totalMinutes));
  const [inputError, setInputError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  // The raw text the clamp last refused. Enter commits and then moves focus, so
  // the blur handler commits the same string a second time; without this the
  // contributor gets the same toast twice for one keystroke.
  const rejectedRawRef = useRef<string | null>(null);

  useEffect(() => {
    // A REFUSED value stays on screen, and stays announced. Blur fires
    // `setIsFocused(false)` and `commit()` in one batch, so an unguarded reset
    // here wiped both the `role="alert"` message and the number the contributor
    // had typed the instant they tabbed away — silently, on the commonest path of
    // all. `onFocus` clears the ref, so re-entering the cell starts clean.
    if (!isFocused && rejectedRawRef.current === null) {
      setValue(cellText(totalMinutes));
      setInputError(null);
    }
  }, [totalMinutes, isFocused]);

  // Returns whether the value was accepted, so the caller can decline to move
  // focus off a cell it just refused. Navigating away would take the inline
  // `role="alert"` with it and land the contributor on a different row with no
  // idea their entry was rejected (WCAG 2.4.3, 3.3.1) — true of the pre-existing
  // parse errors too, not only the clamp.
  const commit = useCallback(
    (raw: string): boolean => {
      if (readOnly) return true;
      const { minutes, error } = parseHours(raw);
      if (error) {
        setInputError(error);
        return false;
      }
      if (maxMinutes !== undefined && clampMessage !== undefined && minutes > maxMinutes) {
        setInputError(clampMessage);
        if (rejectedRawRef.current !== raw) {
          rejectedRawRef.current = raw;
          onReject?.(clampMessage);
        }
        return false;
      }
      rejectedRawRef.current = null;
      setInputError(null);
      setValue(cellText(minutes));
      onCommit(lineId, minutes);
      return true;
    },
    [lineId, onCommit, readOnly, maxMinutes, clampMessage, onReject],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (commit(value)) onNavigate(lineId, 1);
    } else if (e.key === 'Escape') {
      // An explicit abandon: drop the rejection so the reset effect can restore
      // the saved value on blur.
      rejectedRawRef.current = null;
      setValue(cellText(totalMinutes));
      setInputError(null);
      e.currentTarget.blur();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (commit(value)) onNavigate(lineId, 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commit(value)) onNavigate(lineId, -1);
    }
  };

  const hasValue = totalMinutes > 0;
  // One slot in the accessible name, and one source for it. There used to be a
  // second (`readOnlyReason`) that the category rows filled from their own
  // client-derived inactive/locked state; with every reason now arriving from
  // the server, `restrictionNote` is that state, and `restrictionNoteFor`
  // already drops the "can be lowered" clause when the cell is read-only.
  const stateNote = restrictionNote;

  return (
    <div className="day-input-wrap">
      <input
        ref={(el) => registerRef(lineId, el)}
        type="text"
        inputMode="decimal"
        className={`day-input hours-input${hasValue ? ' has-value' : ''}${inputError ? ' error' : ''}`}
        value={value}
        placeholder="—"
        readOnly={readOnly}
        aria-label={`${taskLabel}: hours this week${stateNote ? `, ${stateNote}` : ''}`}
        aria-invalid={inputError !== null ? true : undefined}
        aria-describedby={inputError ? `err-${lineId}` : undefined}
        onChange={(e) => {
          setValue(e.target.value);
          setInputError(null);
          rejectedRawRef.current = null;
        }}
        onFocus={(e) => {
          setIsFocused(true);
          // Re-entering the cell is a fresh attempt: clear the dedupe so the same
          // value refused twice is announced twice, rather than silently.
          rejectedRawRef.current = null;
          e.target.select();
        }}
        onBlur={() => {
          setIsFocused(false);
          commit(value);
        }}
        onKeyDown={handleKeyDown}
      />
      {inputError && (
        <span id={`err-${lineId}`} role="alert" className="sr-only">
          {inputError}
        </span>
      )}
    </div>
  );
}

// ---- CommentButton ----

interface CommentButtonProps {
  note: string | null;
  taskName: string;
  readOnly: boolean;
  isPopoverOpen: boolean;
  onClick: (rect: DOMRect, ref: React.RefObject<HTMLButtonElement | null>) => void;
}

function CommentButton({ note, taskName, readOnly, isPopoverOpen, onClick }: CommentButtonProps) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const hasNote = !!note;
  // In read-only mode, render nothing if there is no note
  if (readOnly && !hasNote) return null;

  let ariaLabel: string;
  if (hasNote && !readOnly) {
    ariaLabel = `Edit note for ${taskName}`;
  } else if (!hasNote && !readOnly) {
    ariaLabel = `Add note for ${taskName}`;
  } else {
    ariaLabel = `View note for ${taskName}`;
  }

  return (
    <button
      ref={btnRef}
      type="button"
      className={`comment-btn${hasNote ? ' has-comment' : ''}`}
      aria-label={ariaLabel}
      aria-haspopup="dialog"
      aria-expanded={isPopoverOpen}
      onClick={() => {
        const rect = btnRef.current?.getBoundingClientRect() ?? new DOMRect(0, 0, 0, 0);
        onClick(rect, btnRef);
      }}
      data-testid="line-note-btn"
      data-has-note={String(hasNote)}
    >
      {hasNote ? (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="currentColor"
          stroke="none"
          aria-hidden="true"
        >
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      ) : (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      )}
    </button>
  );
}

// ---- ApproverOnlyBadge ----

// The rule in full, for the tooltip. The visible label is the short "Approvers
// only"; this is where the accurate rule lives, because administrators hold
// these rows too and are exempt from the team-edit demotion that produces a
// retained line.
const APPROVER_ONLY_TOOLTIP =
  'Approvers only — this category is seeded onto approvers’ and administrators’ timesheets, not onto contributors’';

// Shown on a RETAINED row only — a line whose owner no longer qualifies for the
// category but whose hours keep it on the sheet. It is the only thing on that
// row that says why it is dimmed and will not take new hours, so it is not
// decoration: the dimming is (WCAG 1.4.1), the TEXT is not.
//
// An approver's own open approver-only row carries NO badge. The handoff put its
// `.appr-only-tag` pill on both, but telling an approver that a category on
// their own timesheet is for approvers is noise, and the inline pill existed
// only to serve that case without growing the row. With one case left this is a
// `.cat-state-badge` like Inactive and Locked — three restricted category states
// that now look and stack alike, rather than two conventions.
function ApproverOnlyBadge() {
  return (
    <span className="cat-state-badge cat-state-badge--approver-only" title={APPROVER_ONLY_TOOLTIP}>
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
      {RESTRICTED_BADGE_LABEL.approver_only}
    </span>
  );
}

// ---- TimesheetGrid ----

interface TimesheetGridProps {
  timesheet: TimesheetResponse;
  categories: AdminCategoryItem[];
  committedOverrides: Map<string, number>;
  onCellCommit: (lineId: string, minutes: number) => void;
  // Lines a save was just refused on with a terminal 409, keyed to the server's
  // own message. Clamps the cell straight away so the next keystroke cannot
  // re-fire a doomed request, and is replaced by the server's own
  // `restrictedReason` as soon as the refetch lands.
  locallyRestricted: Map<string, string>;
  // A value the clamp refused, for a toast that outlives the cell's focus.
  onCellReject: (message: string) => void;
  readOnly: boolean;
  cellRefs: React.MutableRefObject<Map<string, HTMLInputElement>>;
  lineIds: string[];
  onNoteClick: (
    lineId: string,
    note: string | null,
    taskName: string,
    anchorRect: DOMRect,
    buttonRef: React.RefObject<HTMLButtonElement | null>,
  ) => void;
  openNoteLineId: string | null;
}

function TimesheetGrid({
  timesheet,
  categories,
  committedOverrides,
  onCellCommit,
  locallyRestricted,
  onCellReject,
  readOnly,
  cellRefs,
  lineIds,
  onNoteClick,
  openNoteLineId,
}: TimesheetGridProps) {
  const registerRef = useCallback(
    (lineId: string, el: HTMLInputElement | null) => {
      if (el) {
        cellRefs.current.set(lineId, el);
      } else {
        cellRefs.current.delete(lineId);
      }
    },
    [cellRefs],
  );

  const handleNavigate = useCallback(
    (lineId: string, direction: 1 | -1) => {
      const idx = lineIds.indexOf(lineId);
      if (idx === -1) return;
      const nextId = lineIds[idx + direction];
      if (nextId !== undefined) {
        cellRefs.current.get(nextId)?.focus();
      }
    },
    [lineIds, cellRefs],
  );

  const categoryMap = new Map(categories.map((c) => [c.categoryId, c]));

  // Group admin-category lines by category (non-project time)
  const seenCategories: string[] = [];
  const linesByCategory = new Map<string, TimesheetResponse['lines']>();
  for (const line of timesheet.lines) {
    const catId = line.adminCategoryId;
    if (!catId) continue;
    if (!linesByCategory.has(catId)) {
      seenCategories.push(catId);
      linesByCategory.set(catId, []);
    }
    linesByCategory.get(catId)?.push(line);
  }

  // Group project-task lines by projectId (stable) with projectName as display fallback
  const seenProjectKeys: { key: string; displayName: string }[] = [];
  const linesByProject = new Map<string, TimesheetResponse['lines']>();
  for (const line of timesheet.lines) {
    if (!line.assignmentId) continue;
    const key = line.projectId ?? line.projectName ?? 'unknown';
    const displayName = line.projectName ?? 'Unknown project';
    if (!linesByProject.has(key)) {
      seenProjectKeys.push({ key, displayName });
      linesByProject.set(key, []);
    }
    linesByProject.get(key)?.push(line);
  }

  const weekTotal = timesheet.lines.reduce((sum, line) => {
    const override = committedOverrides.get(line.lineId);
    return (
      sum +
      (override !== undefined ? override : line.entries.reduce((s, e) => s + e.regularMinutes, 0))
    );
  }, 0);

  return (
    <div className="grid-card featured" role="region" aria-label="Timesheet entries">
      <div className="ts-grid" role="grid" aria-label="Timesheet">
        {/* Column headers */}
        <div role="row" style={{ display: 'contents' }}>
          <div className="cell head" role="columnheader">
            Project / Task
          </div>
          <div className="cell head" role="columnheader">
            Type
          </div>
          <div className="cell head" role="columnheader">
            Hours this week
          </div>
          <div className="cell head" role="columnheader">
            <span className="sr-only">Notes</span>
          </div>
        </div>

        {/* Project task groups */}
        {seenProjectKeys.map(({ key, displayName }) => {
          const lines = linesByProject.get(key) ?? [];
          const projTotal = lines.reduce((sum, line) => {
            const override = committedOverrides.get(line.lineId);
            return (
              sum +
              (override !== undefined
                ? override
                : line.entries.reduce((s, e) => s + e.regularMinutes, 0))
            );
          }, 0);

          return (
            <Fragment key={key}>
              <div role="row" style={{ display: 'contents' }}>
                <div className="cell proj-head span" role="rowheader">
                  {displayName}
                  {projTotal > 0 && <span className="proj-total">{formatHours(projTotal)}h</span>}
                </div>
              </div>

              {lines.map((line, indexInGroup) => {
                const serverMinutes = line.entries.reduce((s, e) => s + e.regularMinutes, 0);
                const displayMinutes = committedOverrides.get(line.lineId) ?? serverMinutes;
                const taskName = line.taskName ?? 'Unnamed task';
                const lineNote = line.description ?? null;
                // Restricted assignment rows reuse the inactive-category
                // treatment exactly — same dimmed cells, same badge shape —
                // rather than inventing a second visual language for "closed".
                // The badge TEXT carries the meaning; the dimming is decoration
                // (WCAG 1.4.1), and the row itself never disappears.
                const restriction = restrictionOf(line);
                // The server's message from a just-refused save, standing in
                // until the refetch replaces it with a reason.
                const pendingMessage = locallyRestricted.get(line.lineId);
                const clampMessage =
                  restriction !== null
                    ? RESTRICTED_MESSAGE[restriction]({
                        projectName: line.projectName ?? displayName,
                        taskName,
                        categoryName: null,
                      })
                    : pendingMessage;
                // Interactive, NOT disabled: the ceiling is what the line
                // already records, so lowering and clearing still work and an
                // approver's "cut this to 20h" stays compliable.
                const maxMinutes = clampMessage !== undefined ? displayMinutes : undefined;
                const cellCls = (base: string) =>
                  `${base}${restriction !== null ? ' is-inactive' : ''}`;

                return (
                  <div
                    key={line.lineId}
                    role="row"
                    className={taskRowClassName({
                      indexInGroup,
                      isNoteOpen: openNoteLineId === line.lineId,
                    })}
                    style={{ display: 'contents' }}
                  >
                    <div className={cellCls('cell task-cell')} role="gridcell">
                      <span className="task-name" title={taskName}>
                        {taskName}
                      </span>
                      {restriction !== null && (
                        <span className="cat-state-badge cat-state-badge--inactive">
                          {RESTRICTED_BADGE_LABEL[restriction]}
                        </span>
                      )}
                      {lineNote && (
                        <div className="note-preview" data-testid="line-note-preview">
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            stroke="none"
                            className="note-preview-glyph"
                            aria-hidden="true"
                          >
                            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                          </svg>
                          <span className="note-preview-text">{lineNote}</span>
                        </div>
                      )}
                    </div>
                    <div className={cellCls('cell cls-cell')} role="gridcell">
                      <span className="cls-tag">{line.classificationName ?? 'Work'}</span>
                    </div>
                    <div className={cellCls('cell hours-cell')} role="gridcell">
                      <HourCell
                        lineId={line.lineId}
                        totalMinutes={displayMinutes}
                        readOnly={readOnly}
                        taskLabel={taskName}
                        maxMinutes={maxMinutes}
                        clampMessage={clampMessage}
                        restrictionNote={restrictionNoteFor(
                          restriction,
                          pendingMessage !== undefined,
                          readOnly,
                        )}
                        onReject={onCellReject}
                        onCommit={onCellCommit}
                        registerRef={registerRef}
                        onNavigate={handleNavigate}
                      />
                    </div>
                    <div className={cellCls('cell comment-cell')} role="gridcell">
                      <CommentButton
                        note={lineNote}
                        taskName={taskName}
                        readOnly={readOnly}
                        isPopoverOpen={openNoteLineId === line.lineId}
                        onClick={(rect, ref) =>
                          onNoteClick(line.lineId, lineNote, taskName, rect, ref)
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </Fragment>
          );
        })}

        {/* Non-project time — a single section, with one line per standing category
            (categories are pre-seeded, one line each), rather than a redundant
            one-line group per category. "Lines never disappear": an inactive
            category that still carries hours stays, dimmed, with an Inactive badge. */}
        {seenCategories.length > 0 &&
          (() => {
            const catLines = seenCategories.flatMap((catId) =>
              (linesByCategory.get(catId) ?? []).map((line) => ({ line, catId })),
            );
            const groupTotal = catLines.reduce((sum, { line }) => {
              const override = committedOverrides.get(line.lineId);
              return (
                sum +
                (override !== undefined
                  ? override
                  : line.entries.reduce((s, e) => s + e.regularMinutes, 0))
              );
            }, 0);
            return (
              <Fragment key="non-project">
                <div role="row" style={{ display: 'contents' }}>
                  <div className="cell proj-head span" role="rowheader">
                    Campus Solutions – General Activities
                    {groupTotal > 0 && (
                      <span className="proj-total">{formatHours(groupTotal)}h</span>
                    )}
                  </div>
                </div>

                {catLines.map(({ line, catId }, indexInGroup) => {
                  const cat = categoryMap.get(catId);
                  const isWork = cat ? cat.isWorkTime : true;
                  // Standing-row states (lines never disappear), read from the
                  // SERVER exactly as the assignment section reads them. The
                  // client-derived `isInactive` / `isCatLocked` branch that used
                  // to live here is gone: it computed from the category catalog
                  // cache, and approver-only additionally needs `me.role`, so
                  // keeping it would have left the grid deciding a row's state
                  // from two independently-stale caches while the API decided it
                  // from neither.
                  const restriction = restrictionOf(line);
                  // Walls vs ratchet. Inactive and locked refuse every value
                  // server-side, so the cell is genuinely read-only.
                  // `approver_only` is a RATCHET — a value at or below what the
                  // line already records still commits — so it stays
                  // interactive and clamps, exactly like a restricted
                  // assignment row. Wiring it to `readOnly` would make the one
                  // correction the API still guarantees ("cut that 6h to 3h" on
                  // a returned sheet) unreachable through the only UI there is.
                  const isWall = isCategoryWall(restriction);
                  const cellReadOnly = readOnly || isWall;
                  const serverMinutes = line.entries.reduce((s, e) => s + e.regularMinutes, 0);
                  const displayMinutes = committedOverrides.get(line.lineId) ?? serverMinutes;
                  const catName = cat?.name ?? 'Unknown category';
                  const lineNote = line.description ?? null;
                  const pendingMessage = locallyRestricted.get(line.lineId);
                  const clampMessage =
                    restriction !== null
                      ? RESTRICTED_MESSAGE[restriction]({
                          projectName: null,
                          taskName: null,
                          categoryName: catName,
                        })
                      : pendingMessage;
                  // Interactive, NOT disabled — see `isWall` above. On a wall
                  // row `readOnly` short-circuits the commit before the clamp is
                  // ever consulted, so this is inert there.
                  const maxMinutes = clampMessage !== undefined ? displayMinutes : undefined;
                  // The approver-only marker, on both rows that deserve it: an
                  // An approver's own open approver-only row is deliberately
                  // unbadged — it would be telling them something they already
                  // know. Only a RETAINED row is marked, and it badges from the
                  // server's own reason rather than from the viewer's role.
                  // Locked keeps its undimmed treatment: the category is still
                  // current, it simply cannot take hours in this period, and the
                  // lock badge carries that. Every other closed state dims. The
                  // dimming is decoration either way — the badge TEXT is what
                  // carries the meaning (WCAG 1.4.1) — and the row never
                  // disappears.
                  const cellCls = (base: string) =>
                    `${base}${restriction !== null && restriction !== 'category_locked' ? ' is-inactive' : ''}`;
                  return (
                    <div
                      key={line.lineId}
                      role="row"
                      className={taskRowClassName({
                        indexInGroup,
                        isNoteOpen: openNoteLineId === line.lineId,
                      })}
                      style={{ display: 'contents' }}
                    >
                      <div className={cellCls('cell task-cell')} role="gridcell">
                        <span className="task-name" title={catName}>
                          {catName}
                        </span>
                        {restriction === 'approver_only' && <ApproverOnlyBadge />}
                        {restriction === 'category_inactive' && (
                          <span className="cat-state-badge cat-state-badge--inactive">
                            {RESTRICTED_BADGE_LABEL.category_inactive}
                          </span>
                        )}
                        {restriction === 'category_locked' && (
                          <span className="cat-state-badge cat-state-badge--locked">
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 12 12"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path d="M9 5H8V3.5a2 2 0 0 0-4 0V5H3a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1zm-3 3.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM7 5H5V3.5a1 1 0 0 1 2 0V5z" />
                            </svg>
                            {RESTRICTED_BADGE_LABEL.category_locked}
                          </span>
                        )}
                        {lineNote && (
                          <div className="note-preview" data-testid="line-note-preview">
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              stroke="none"
                              className="note-preview-glyph"
                              aria-hidden="true"
                            >
                              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                            </svg>
                            <span className="note-preview-text">{lineNote}</span>
                          </div>
                        )}
                      </div>
                      <div className={cellCls('cell cls-cell')} role="gridcell">
                        <span className={`cls-tag${isWork ? '' : ' nonwork'}`}>
                          {categoryTypeLabel(isWork)}
                        </span>
                      </div>
                      <div className={cellCls('cell hours-cell')} role="gridcell">
                        <HourCell
                          lineId={line.lineId}
                          totalMinutes={displayMinutes}
                          readOnly={cellReadOnly}
                          taskLabel={catName}
                          maxMinutes={maxMinutes}
                          clampMessage={clampMessage}
                          restrictionNote={restrictionNoteFor(
                            restriction,
                            pendingMessage !== undefined,
                            cellReadOnly,
                          )}
                          onReject={onCellReject}
                          onCommit={onCellCommit}
                          registerRef={registerRef}
                          onNavigate={handleNavigate}
                        />
                      </div>
                      <div className={cellCls('cell comment-cell')} role="gridcell">
                        <CommentButton
                          note={lineNote}
                          taskName={catName}
                          // The RESTRICTION deliberately does not reach this
                          // control on a retained (approver-only) row: the
                          // server leaves patchLineDescription unguarded because
                          // a note on a retained line is legitimately its
                          // owner's, and `CommentButton` renders NOTHING when
                          // `readOnly` is true and there is no note yet — so
                          // reusing the hours cell's flag here would silently
                          // cancel that permission in the UI. Walls keep their
                          // existing treatment; relaxing those is the same
                          // behaviour change to shipped features that the
                          // wall-to-ratchet follow-up owns.
                          readOnly={cellReadOnly}
                          isPopoverOpen={openNoteLineId === line.lineId}
                          onClick={(rect, ref) =>
                            onNoteClick(line.lineId, lineNote, catName, rect, ref)
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </Fragment>
            );
          })()}

        {/* Footer row — label spans cols 1-2 via CSS, no explicit Type cell needed */}
        <div role="row" style={{ display: 'contents' }}>
          <div className="cell foot label" role="rowheader">
            Week total
          </div>
          <div className="cell foot grand-total" role="gridcell">
            {formatHours(weekTotal)}
            <span className="hours-unit">h</span>
          </div>
          <div className="cell foot" role="gridcell" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

// ---- SaveIndicator ----

function SaveIndicator({ state }: { state: SaveState }) {
  if (state === 'idle') return null;
  const label = state === 'saving' ? 'Saving…' : state === 'saved' ? 'Saved' : 'Save failed';
  return (
    <span className={`save-status ${state}`} aria-live="polite" role="status">
      {state === 'saving' && <span className="pulse" aria-hidden="true" />}
      {label}
    </span>
  );
}

// ---- Confetti ----

const CONFETTI_COLORS = ['#bf5700', '#f8971f', '#ffd600', '#a6cd57', '#00a9b7', '#005f86'] as const;

function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 60 }, (_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.4;
        const dur = 2 + Math.random() * 1.6;
        const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
        const rot = Math.random() * 360;
        return (
          <span
            key={i}
            style={{
              position: 'absolute' as const,
              left: `${left}%`,
              background: color,
              animationDelay: `${delay}s`,
              animationDuration: `${dur}s`,
              transform: `rotate(${rot}deg)`,
              borderRadius: i % 3 === 0 ? '50%' : '1px',
            }}
          />
        );
      }),
    [],
  );

  return (
    <div className="ts-confetti" aria-hidden="true">
      {pieces}
    </div>
  );
}

// ---- CelebrationScreen ----

interface CelebrationScreenProps {
  timesheet: TimesheetResponse;
  note: string | null;
  onViewSheet: () => void;
  onGoNext: () => void;
}

function CelebrationScreen({ timesheet, note, onViewSheet, onGoNext }: CelebrationScreenProps) {
  const weekRange = formatWeekRange(timesheet.period.weekStartDate, timesheet.period.weekEndDate);

  // Honest project/indirect split (same recap the submit dialog showed).
  const recap = computeRecap(timesheet.lines);
  const projectWord = recap.projectCount === 1 ? 'project' : 'projects';

  return (
    // role="status" announces the submission outcome (including the hours split
    // and any echoed note) to assistive tech, since focus otherwise falls to body
    // when the action bar unmounts.
    <div className="ts-celebrate" role="status">
      <Confetti />
      <div className="ts-celebrate-check" aria-hidden="true">
        <svg
          width="44"
          height="44"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h2 className="ts-celebrate-heading">Timesheet submitted.</h2>
      <p
        style={{
          color: 'var(--fg3)',
          fontSize: 'var(--fs-16)',
          maxWidth: 540,
          margin: '0 auto var(--space-4)',
          lineHeight: 'var(--lh-relaxed)',
        }}
      >
        Your hours for the week of <strong>{weekRange}</strong> are on their way to your approver.
        You&apos;ll get an email when they sign off — usually within a business day.
      </p>
      <div className="ts-celebrate-recap">
        Submitted <strong>{formatHours(recap.totalMinutes)} hours</strong>
        {recap.projectCount > 0 && recap.indirectMinutes > 0 && (
          <>
            {' '}
            — {formatHours(recap.projectMinutes)}h across {recap.projectCount} {projectWord} ·{' '}
            {formatHours(recap.indirectMinutes)}h indirect
          </>
        )}
        {recap.projectCount > 0 && recap.indirectMinutes === 0 && (
          <>
            {' '}
            across {recap.projectCount} {projectWord}
          </>
        )}
        {recap.projectCount === 0 && <> — all leave &amp; non-project</>}
      </div>
      {note && (
        <div className="celebrate-note">
          <span className="celebrate-note__label">Note sent to your approver</span>
          <p>{note}</p>
        </div>
      )}
      <div style={{ display: 'inline-flex', gap: 8, marginTop: 'var(--space-4)' }}>
        <button type="button" className="btn btn--secondary" onClick={onViewSheet}>
          View submitted sheet
        </button>
        <button
          type="button"
          className="btn btn--primary"
          onClick={onGoNext}
          // TODO: advance to next week once week navigation (2026-05-28-001) ships
        >
          Start next week →
        </button>
      </div>
    </div>
  );
}

// ---- ActionBar ----

interface ActionBarProps {
  weekTotal: number;
  weeklyMinuteTarget: number;
  saveState: SaveState;
  status: UiStatus;
  onRequestSubmit: () => void;
  onRecall: () => void;
  isSubmitting: boolean;
  isRecalling: boolean;
  periodClosed: boolean;
}

function ActionBar({
  weekTotal,
  weeklyMinuteTarget,
  saveState,
  status,
  onRequestSubmit,
  onRecall,
  isSubmitting,
  isRecalling,
  periodClosed,
}: ActionBarProps) {
  const canSubmit = weekTotal >= weeklyMinuteTarget;
  const targetHours = formatHours(weeklyMinuteTarget);
  const shortfallMinutes = !canSubmit && weekTotal > 0 ? weeklyMinuteTarget - weekTotal : 0;

  return (
    <div className="action-bar" role="region" aria-label="Timesheet actions">
      <SaveIndicator state={saveState} />
      <div className="summary" aria-live="polite" aria-atomic="true">
        <span className="label">Week total</span>
        <div className="summary-num">
          <span
            className="total"
            data-testid="week-total"
            aria-label={`${formatHours(weekTotal)} of ${targetHours} hours`}
          >
            {formatHours(weekTotal)}
          </span>
          <span className="target" aria-hidden="true">
            h/{targetHours}h
          </span>
        </div>
        <div className="submit-shortfall">
          {shortfallMinutes > 0 ? `${formatHours(shortfallMinutes)}h more needed` : ''}
        </div>
      </div>
      {(status === 'open' || status === 'returned') && (
        <button
          type="button"
          className="btn lg primary"
          onClick={onRequestSubmit}
          disabled={isSubmitting || !canSubmit || periodClosed}
          aria-busy={isSubmitting}
          data-testid="submit-timesheet"
        >
          {status === 'returned' ? 'Re-submit for approval' : 'Submit for approval'}
        </button>
      )}
      {(status === 'submitted' || status === 'approved') && (
        <button
          type="button"
          className="btn lg btn--secondary"
          onClick={onRecall}
          disabled={isRecalling || periodClosed}
          aria-busy={isRecalling}
          data-testid="recall-timesheet"
        >
          {isRecalling ? 'Recalling…' : 'Recall'}
        </button>
      )}
    </div>
  );
}

// ---- Empty state ----

function EmptyState() {
  return (
    <div className="empty-state-card" role="region" aria-label="No timesheet entries">
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <rect x="8" y="8" width="32" height="32" rx="4" stroke="currentColor" strokeWidth="2" />
        <line x1="14" y1="18" x2="34" y2="18" stroke="currentColor" strokeWidth="2" />
        <line x1="14" y1="24" x2="28" y2="24" stroke="currentColor" strokeWidth="2" />
        <line x1="14" y1="30" x2="22" y2="30" stroke="currentColor" strokeWidth="2" />
      </svg>
      <h2>No assignments yet</h2>
      <p>Your manager will add lines to your timesheet. Check back soon.</p>
    </div>
  );
}

// ---- Skeleton ----

function TimesheetSkeleton() {
  return (
    <div aria-live="polite" aria-busy="true">
      <div className="skeleton skeleton--heading" aria-hidden="true" />
      <div className="skeleton skeleton--line" aria-hidden="true" />
      <div className="skeleton skeleton--line" aria-hidden="true" />
      <div className="skeleton skeleton--line" aria-hidden="true" />
      <span className="sr-only">Loading timesheet…</span>
    </div>
  );
}

// ---- Main page ----

interface OpenNoteState {
  lineId: string;
  note: string | null;
  taskName: string;
  anchorRect: DOMRect;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
}

export function Timesheet() {
  const [searchParams, setSearchParams] = useSearchParams();
  const weekParam = searchParams.get('week');

  // The forward cap is next Monday — contributors may start next week early but
  // not draft two or more weeks ahead. Computed every render (not memoised for
  // the session) so it advances correctly if the calendar week rolls over while
  // the page is left open. Both are cheap pure string computations.
  const nextWeekCap = addWeeks(todayMondayIso(), 1);

  // A valid `?week=` means the user picked a week explicitly (navigation, deep
  // link, refresh). Its presence switches off the last-week landing redirect so
  // manual choices — including landing on the current week on purpose — are
  // never overridden. A malformed value is treated as "no explicit week".
  const hasExplicitWeek = Boolean(weekParam && isIsoDate(weekParam));

  // The active week is always a canonical Monday, additionally clamped to the
  // forward cap so a hand-edited or shared `?week=` two-plus weeks ahead cannot
  // fetch (and thereby create) a far-future draft. An absent or malformed value
  // defaults to the current week.
  const weekStartDate = useMemo(() => {
    const monday = weekParam && isIsoDate(weekParam) ? getMondayOf(weekParam) : todayMondayIso();
    return monday > nextWeekCap ? nextWeekCap : monday;
  }, [weekParam, nextWeekCap]);

  // The immediately prior week's Monday. Recomputed each render (like the
  // forward cap) so it tracks the calendar if the week rolls over while the
  // page is left open.
  const lastWeekMonday = addWeeks(todayMondayIso(), -1);

  const disableNext = weekStartDate >= nextWeekCap;

  // "An attention pulse is owed." Armed by the settled-set effect below when the
  // backlog introduces a week this tab session hasn't pulsed for; see there for
  // the full rule.
  const [pulseArmed, setPulseArmed] = useState(false);

  // True once the user has navigated, whether or not the backlog had settled
  // yet. A click on the back arrow before the first settle would otherwise
  // spend an arm that does not exist yet (`armedKeyRef` is still null), and the
  // arm effect would then fire on the first settle and pulse on the week the
  // user had *just* navigated to. See the arm effect below.
  const navigatedBeforeArmRef = useRef(false);

  // Focus destination after a popover row navigates: the week we are heading to,
  // or `null` when nothing is pending. See the consuming effect below for why it
  // is the week string rather than a boolean.
  const pendingWeekFocusRef = useRef<string | null>(null);

  const goToWeek = useCallback(
    (monday: string, opts?: { focusHeading?: boolean }) => {
      // Navigating spends the pulse. It is a one-shot arrival cue for weeks the
      // contributor hasn't been shown yet, not a state that follows them around
      // the calendar — without this, stepping into the backlog and back would
      // replay the animation for weeks they have already seen. The landing
      // redirect writes `?week=` directly rather than through here, so it can
      // still pulse on the week it lands on.
      setPulseArmed(false);
      navigatedBeforeArmRef.current = true;
      // Any navigation supersedes a focus destination left pending by an earlier
      // one, so this is assigned (not merely set) on every call.
      pendingWeekFocusRef.current = opts?.focusHeading === true ? monday : null;
      setSearchParams({ week: monday });
    },
    [setSearchParams],
  );

  // Keep the URL canonical so the address bar matches what is actually shown:
  // a valid non-Monday `?week` (e.g. a shared link to a Wednesday) is rewritten
  // to its Monday, and a link beyond the forward cap is rewritten down to the
  // cap.
  useEffect(() => {
    if (weekParam && isIsoDate(weekParam)) {
      const monday = getMondayOf(weekParam);
      const clamped = monday > nextWeekCap ? nextWeekCap : monday;
      if (clamped !== weekParam) {
        setSearchParams({ week: clamped }, { replace: true });
      }
    }
  }, [weekParam, nextWeekCap, setSearchParams]);

  const meQuery = useMe();
  // Read-only status of last week, fetched only when the user hasn't picked a
  // week explicitly. This hits GET /timesheets/status, which never creates a
  // timesheet — so merely deciding where to land can't materialise a draft or
  // write an audit event for a week the contributor never opened.
  const lastWeekProbe = useWeekStatus(lastWeekMonday, { enabled: !hasExplicitWeek });

  // Weeks *earlier* than last week that still need action — the backlog behind
  // the week-back arrow. Always enabled and deliberately absent from the
  // `isLoading`/`isError` composition below: a decorative indicator must never
  // blank the timesheet when it fails, nor hold it back while it is in flight.
  // While pending or errored `data` is undefined, which collapses the nudge to
  // "not shown" without a `0` flash.
  const outstandingQuery = useOutstandingWeeks();
  const allOutstandingWeeks = outstandingQuery.data?.weeks ?? [];
  const allOutstandingTotal = outstandingQuery.data?.totalCount ?? 0;

  // Last-week landing: when the contributor arrives without choosing a week and
  // last week still needs their action, open last week instead of the current
  // one — nudging them to finish it before it slips. Done (submitted/approved)
  // weeks, and un-started weeks for someone with nothing assigned, leave the
  // default on the current week. Only the immediately prior week is checked.
  //
  // The decision is made only on a *settled, successful* probe: react-query
  // retains the last `data` while refetching or after an error, so gating on
  // `!isFetching && !isError` ensures we never redirect off stale or failed
  // status. `decidingLandingWeek` then keeps the skeleton up (and the
  // current-week query deferred) until the decision resolves; a probe error
  // resolves immediately (retry:false) and leaves the default on the current
  // week.
  const shouldOpenLastWeek =
    !hasExplicitWeek &&
    !lastWeekProbe.isFetching &&
    !lastWeekProbe.isError &&
    lastWeekProbe.data !== undefined &&
    needsLastWeekAttention(lastWeekProbe.data);
  const decidingLandingWeek =
    !hasExplicitWeek && (lastWeekProbe.isLoading || lastWeekProbe.isFetching || shouldOpenLastWeek);

  // The current-week get-or-create query is deferred while the landing week is
  // still undecided. Without this, a bare landing that ends up redirecting to
  // last week would still fire GET /timesheets/current and create (+ audit) a
  // current-week draft the contributor never chose to open — the same class of
  // side effect the read-only last-week probe exists to avoid.
  const timesheetQuery = useTimesheet(weekStartDate, { enabled: !decidingLandingWeek });
  const categoriesQuery = useAdminCategories();
  const updateEntry = useUpdateEntry(weekStartDate);
  const submitTimesheet = useSubmitTimesheet(weekStartDate);
  const recallTimesheet = useRecallTimesheet(weekStartDate);
  const patchLineDescription = usePatchLineDescription(weekStartDate);
  // Pulled out so the terminal-save-failure branch below can depend on a stable
  // reference rather than on the whole query object.
  const refetchTimesheet = timesheetQuery.refetch;
  // The catalog is pulled fresh alongside the week on a terminal save failure.
  // It is not decoration: a non-project row's label, and the approver-only
  // badge, come from here, so a 409 saying "this category is now approvers-only"
  // is precisely the evidence that this cache is stale. Refetching only the week
  // would leave the row looking editable and refusing every retry.
  const refetchCategories = categoriesQuery.refetch;

  useEffect(() => {
    if (shouldOpenLastWeek) {
      setSearchParams({ week: lastWeekMonday }, { replace: true });
    }
  }, [shouldOpenLastWeek, lastWeekMonday, setSearchParams]);

  // A *value* key for the settled backlog set, so the pulse gate below fires
  // once per distinct set of weeks rather than once per object identity.
  // (react-query's structural sharing usually keeps `data` referentially stable
  // across a refetch that returns the same rows, but the gate must not depend on
  // that.) `null` while pending or errored — there is no settled set to judge.
  const outstandingKey =
    outstandingQuery.data === undefined
      ? null
      : outstandingQuery.data.weeks.map((w) => w.weekStartDate).join(' ');

  // Pulse replay gate: one pulse per page load. Arm on the first settled set this
  // page instance sees, and never again for its lifetime — `goToWeek` spends the
  // arm on the first navigation, and a hard refresh or a trip out to Approvals
  // and back is a fresh mount, so the cue reappears exactly when someone arrives
  // at the screen.
  //
  // Arming happens once, on the first settled set. After that any *change* to the
  // set spends the arm rather than renewing it, which is what keeps the cue from
  // firing right after a successful submit: `PeriodBar` is unmounted while the
  // celebration screen shows, so an arm still live when it remounts would replay
  // the animation and nag the contributor for making progress. A focus refetch
  // that returns identical weeks leaves the key untouched and changes nothing.
  //
  // Deliberately a ref and not sessionStorage. An earlier version recorded which
  // weeks had already pulsed so the cue fired at most once per week per tab; it
  // read as too quiet, and it carried a subtle hazard — the effect wrote to the
  // record it read, so `<StrictMode>`'s double-invoked mount effects found the
  // weeks they had just recorded and disarmed the pulse they had just armed,
  // killing the animation in dev and in the fidelity harness. This version is
  // idempotent under a repeated key, so that hazard is gone with it.
  //
  // The accepted trade: backlog arriving mid-session (an approver returning a
  // week while the page sits open) updates the badge without pulsing. It shows on
  // the next load or navigation.
  const armedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (outstandingKey === null) return; // still pending or errored — nothing settled to judge
    if (armedKeyRef.current === null) {
      armedKeyRef.current = outstandingKey;
      // A navigation that beat the first settle has already spent the arm. Record
      // the key so the gate closes (a later change to the set is a change, not a
      // first sighting), but do not arm — pulsing here would fire on the week the
      // user just chose, which is the opposite of an arrival cue.
      if (!navigatedBeforeArmRef.current) setPulseArmed(true);
      return;
    }
    if (armedKeyRef.current !== outstandingKey) setPulseArmed(false);
  }, [outstandingKey]);

  const handleSelectOutstandingWeek = useCallback(
    (monday: string) => {
      goToWeek(monday, { focusHeading: true });
    },
    [goToWeek],
  );

  // The nudge answers one question — "how much further back do I still need to
  // go?" — so both the count and the list are relative to the week on screen,
  // never absolute. Standing on the oldest outstanding week with a badge reading
  // "1" would promise that the back arrow leads somewhere actionable when it
  // does not. Strictly earlier than the viewed week: the week you are already
  // looking at is not somewhere you need to navigate to.
  //
  // `weeks` is server-capped (OUTSTANDING_ROW_CAP) while `totalCount` is not, so
  // the rows the server withheld have to be added back or the count would
  // undercount a deep backlog. Every withheld week is older than every returned
  // one, which fixes the error direction:
  //
  //   - viewed week at or after the oldest returned row → exact, every withheld
  //     week really is earlier than what is on screen;
  //   - viewed week below the oldest returned row → may overstate, because some
  //     of the withheld weeks may now be at or after the viewed week.
  //
  // It is added unconditionally, and that asymmetry is deliberate: gating it on
  // "the shortfall is provably earlier" collapses the count to *zero* on any week
  // older than every returned row (`outstandingWeeks` is empty there too), which
  // hides a real backlog behind a vanished nudge. Overstating sends the user one
  // click too far back; understating tells them there is nothing to go back for.
  // Fail safe toward showing — same rule as "lines never disappear". Both cases
  // need a backlog deeper than the cap, where the badge already reads "9+".
  const outstandingWeeks = useMemo(
    () => allOutstandingWeeks.filter((w) => w.weekStartDate < weekStartDate),
    [allOutstandingWeeks, weekStartDate],
  );
  const outstandingTotal = useMemo(() => {
    const uncapped = allOutstandingTotal - allOutstandingWeeks.length;
    return outstandingWeeks.length + uncapped;
  }, [allOutstandingTotal, allOutstandingWeeks, outstandingWeeks]);

  // Two conditions, not one. The pulse quiets once the contributor steps back
  // into the backlog, but the badge and popover stay reachable there so they can
  // walk on to the next outstanding week — collapsing these into a single flag
  // would make the feature vanish exactly while it is being used. ISO date
  // strings compare lexicographically, the existing idiom in this file.
  const showPulse = pulseArmed && outstandingTotal > 0 && weekStartDate >= lastWeekMonday;
  const showPopover = outstandingTotal > 0;

  const [committedOverrides, setCommittedOverrides] = useState<Map<string, number>>(new Map());
  // Lines whose last save came back with a terminal 409, keyed to the server's
  // own message. Held only until the refetch below lands and replaces them with
  // the server's `restrictedReason`: it stops the very next keystroke re-firing a
  // request that cannot succeed, without the client inventing a reason it was
  // never told. Deliberately no badge — an unexplained dimmed row would be state
  // carried by colour alone (WCAG 1.4.1); the toast carries the explanation.
  const [locallyRestricted, setLocallyRestricted] = useState<Map<string, string>>(new Map());
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { toasts, pushToast, dismiss: dismissToast } = useToasts();
  const cellRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  const [openNote, setOpenNote] = useState<OpenNoteState | null>(null);
  const [celebrating, setCelebrating] = useState(false);
  const [periodClosed, setPeriodClosed] = useState(false);
  // Submit confirmation dialog + the note captured at submit (held only to show
  // it back on the celebration screen).
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submittedNote, setSubmittedNote] = useState<string | null>(null);

  usePageTitle('My timesheet');

  // Sync lock state from the server on load (and whenever the timesheet reloads)
  useEffect(() => {
    const data = timesheetQuery.data;
    if (!data) return;
    setPeriodClosed(data.isLocked);

    // Reconcile the client-only clamps a 409 set against fresh server state.
    // The server's own `restrictedReason` is the authority on whether a row is
    // still closed; the clamp exists only to stop the next keystroke re-firing
    // a request we already know will fail, and it stops earning its keep the
    // moment the server says otherwise (project restored, contributor
    // reassigned). Reconciling here rather than clearing on a successful save
    // matters because a reduction succeeds on a restricted row by design — that
    // is not evidence the row reopened.
    //
    // Returns the same map when nothing changed so React bails out of the
    // re-render; `data` is a new object each render, so an unconditional new
    // Map here would loop until the heap gives out.
    setLocallyRestricted((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Map(prev);
      for (const line of data.lines) {
        if (next.has(line.lineId) && line.assignmentActive && line.restrictedReason === null) {
          next.delete(line.lineId);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [timesheetQuery.data]);

  // Reset overrides and celebration state when the user or viewed week changes.
  // Keying on weekStartDate (not just the loaded timesheetId) clears the
  // celebration immediately on navigation, before the next week's data arrives.
  useEffect(() => {
    setCommittedOverrides(new Map());
    setLocallyRestricted(new Map());
    setCelebrating(false);
    setSubmitOpen(false);
    setSubmittedNote(null);
  }, [timesheetQuery.data?.timesheetId, meQuery.data?.userId, weekStartDate]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current !== undefined) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const handleCellCommit = useCallback(
    (lineId: string, minutes: number) => {
      if (periodClosed) return;
      setCommittedOverrides((prev) => new Map(prev).set(lineId, minutes));
      setSaveState('saving');
      if (saveTimerRef.current !== undefined) clearTimeout(saveTimerRef.current);

      updateEntry.mutate(
        { lineId, totalMinutes: minutes },
        {
          onSuccess: () => {
            setSaveState('saved');
            saveTimerRef.current = setTimeout(() => setSaveState('idle'), 2500);
            // Deliberately does NOT clear this line's clamp. A save landing does
            // not prove the row is open: the server accepts REDUCTIONS on a
            // restricted row on purpose, so clearing here would drop the clamp
            // the moment someone lowered their hours and let the very next
            // increase fire a request that can only 409. The clamp is
            // reconciled against fresh server state instead — see the effect
            // that reads timesheetQuery.data.
          },
          onError: (err) => {
            setCommittedOverrides((prev) => {
              const next = new Map(prev);
              next.delete(lineId);
              return next;
            });
            setSaveState('error');

            // 404 and 409 are verdicts, not blips: the line was swept away, the
            // sheet is no longer editable, or the row has stopped accepting new
            // hours. "Please try again" is a lie there, and dropping the override
            // silently discards the number the contributor typed. So: say what
            // the server said, keep their figure in the message, and pull the
            // week fresh so the row comes back wearing its real state and badge.
            // Same shape as the periodClosed branch in handleSubmit below.
            const status = err instanceof ApiError ? err.status : null;
            if (status === 404 || status === 409) {
              const reason =
                (err instanceof ApiError ? err.apiMessage : undefined) ??
                'That line can no longer take this change.';
              // 409 means the row is closed but still there; 404 means it is
              // gone and the refetch will drop it, so there is nothing to clamp.
              if (status === 409) {
                setLocallyRestricted((prev) => new Map(prev).set(lineId, reason));
              }
              pushToast(`${reason} Your entry of ${formatHours(minutes)}h was not saved.`, 'error');
              void refetchTimesheet();
              void refetchCategories();
            } else {
              pushToast('Failed to save. Please try again.', 'error');
            }
            saveTimerRef.current = setTimeout(() => setSaveState('idle'), 3000);
          },
        },
      );
    },
    [updateEntry, pushToast, periodClosed, refetchTimesheet, refetchCategories],
  );

  // A clamp refusal reaches the contributor twice: an inline alert on the cell
  // while it has focus, and this toast, which outlives the blur that resets the
  // cell back to its saved value. 'warn' (charcoal + tangerine) rather than
  // 'error' — nothing broke, the row simply cannot go up.
  const handleCellReject = useCallback(
    (message: string) => {
      pushToast(message, 'warn');
    },
    [pushToast],
  );

  const handleSubmit = useCallback(
    (note: string) => {
      const comment = note.trim() ? note.trim() : null;
      submitTimesheet.mutate(
        { comment },
        {
          onSuccess: () => {
            setSubmittedNote(comment);
            setSubmitOpen(false);
            setCelebrating(true);
            // Entering the celebration spends the arm. `PeriodBar` unmounts for
            // the duration, so an arm still live when "View submitted sheet"
            // remounts it would replay the 3.6s animation — nagging someone for
            // having just submitted. The backlog set is *not* a reliable spend
            // here: submitting the current week or last week changes nothing in
            // it (neither was ever in the backlog), so `outstandingKey` stays put
            // and the set-changed branch of the arm gate never runs.
            setPulseArmed(false);
          },
          onError: (err) => {
            if (isReportingPeriodClosedError(err)) {
              setPeriodClosed(true);
              // A closed period can never succeed — close the dialog so the
              // banner is visible and the user can't retry a doomed submit.
              setSubmitOpen(false);
              pushToast('This period is closed. Contact an administrator.', 'error');
            } else {
              pushToast('Failed to submit. Please try again.', 'error');
            }
          },
        },
      );
    },
    [submitTimesheet, pushToast],
  );

  const handleRecall = useCallback(() => {
    recallTimesheet.mutate(undefined, {
      onSuccess: () => pushToast('Timesheet recalled.', 'warn'),
      onError: (err) => {
        if (isReportingPeriodClosedError(err)) {
          setPeriodClosed(true);
          pushToast('This period is closed. Contact an administrator.', 'error');
        } else {
          pushToast('Failed to recall. Please try again.', 'error');
        }
      },
    });
  }, [recallTimesheet, pushToast]);

  const handleNoteClick = useCallback(
    (
      lineId: string,
      note: string | null,
      taskName: string,
      anchorRect: DOMRect,
      buttonRef: React.RefObject<HTMLButtonElement | null>,
    ) => {
      if (periodClosed) return;
      setOpenNote({ lineId, note, taskName, anchorRect, buttonRef });
    },
    [periodClosed],
  );

  const handleNoteClose = useCallback(() => {
    setOpenNote((prev) => {
      prev?.buttonRef.current?.focus();
      return null;
    });
  }, []);

  const handleNoteSave = useCallback(
    (description: string | null) => {
      if (!openNote) return;
      // Capture lineId now; the user may open a different line's popover while
      // the PATCH is in flight, and onSuccess should only close the matching one.
      const savingLineId = openNote.lineId;
      patchLineDescription.mutate(
        { lineId: savingLineId, description },
        {
          onSuccess: () => {
            setOpenNote((prev) => {
              if (prev?.lineId !== savingLineId) return prev;
              prev.buttonRef.current?.focus();
              return null;
            });
          },
          onError: () => {
            pushToast('Failed to save note. Please try again.', 'error');
          },
        },
      );
    },
    [openNote, patchLineDescription, pushToast],
  );

  const isLoading =
    meQuery.isLoading ||
    timesheetQuery.isLoading ||
    categoriesQuery.isLoading ||
    decidingLandingWeek;
  const isError = meQuery.isError || timesheetQuery.isError || categoriesQuery.isError;

  // Focus destination after a popover row navigates. `PeriodBar` unmounts while
  // the chosen week loads and the activated row disappears with it, so without an
  // explicit destination focus lands on `<body>` and a keyboard user loses their
  // place (WCAG 2.4.3). The week-range heading only exists once the new week has
  // rendered, so this runs after every render until it finds it.
  //
  // The pending value is the *destination week*, not a boolean, and that is what
  // bounds its lifetime. A boolean set before navigating could only be cleared by
  // a successful render of the heading; if the chosen week's query errored the
  // heading never mounted, the flag survived for the life of the mount, and some
  // later unrelated render — the load succeeding on retry, a save settling —
  // stole focus, plausibly out of an hour input mid-typing. Keyed to the week, it
  // is consumed only by the render it was created for and dropped the moment that
  // render becomes impossible.
  useEffect(() => {
    const target = pendingWeekFocusRef.current;
    if (target === null) return;
    // A superseding navigation (or a URL clamp) moved us off the pending week.
    if (target !== weekStartDate) {
      pendingWeekFocusRef.current = null;
      return;
    }
    // The heading will never mount for a week that failed to load, so the
    // pending focus must not outlive the failure.
    if (isError) {
      pendingWeekFocusRef.current = null;
      return;
    }
    const heading = document.getElementById(WEEK_RANGE_HEADING_ID);
    if (!heading) return;
    pendingWeekFocusRef.current = null;
    heading.focus();
  });

  const retry = () => {
    if (meQuery.isError) void meQuery.refetch();
    if (timesheetQuery.isError) void timesheetQuery.refetch();
    if (categoriesQuery.isError) void categoriesQuery.refetch();
  };

  const timesheet = timesheetQuery.data;
  const categories = categoriesQuery.data ?? [];
  const uiStatus = timesheet ? apiStatusToUi(timesheet.status) : 'open';
  const weekTotal = timesheet
    ? timesheet.lines.reduce((sum, line) => {
        const override = committedOverrides.get(line.lineId);
        return (
          sum +
          (override !== undefined
            ? override
            : line.entries.reduce((s, e) => s + e.regularMinutes, 0))
        );
      }, 0)
    : 0;

  const lineIds = timesheet?.lines.map((l) => l.lineId) ?? [];

  // Hours recap for the submit dialog + celebration (override-aware so its total
  // matches the action-bar weekTotal).
  const recap = useMemo(
    () => (timesheet ? computeRecap(timesheet.lines, committedOverrides) : null),
    [timesheet, committedOverrides],
  );
  const weekRange = timesheet
    ? formatWeekRange(timesheet.period.weekStartDate, timesheet.period.weekEndDate)
    : '';

  return (
    <Layout>
      <div className="page-header">
        <h1>Timesheet</h1>
      </div>

      {/* Persistent live region (always mounted, independent of the loading
          skeleton) so screen readers hear the week change the moment the user
          navigates — satisfies WCAG 4.1.3 even when the next week is uncached.
          Held empty while the landing week is still being decided so the first
          thing announced is the week we actually settle on, never a transient
          current-week value that a redirect to last week is about to replace. */}
      <div className="sr-only" role="status" aria-live="polite">
        {!decidingLandingWeek &&
          `Viewing week of ${formatWeekRange(weekStartDate, weekEndOf(weekStartDate))}`}
      </div>

      {isLoading && <TimesheetSkeleton />}

      {isError && !isLoading && (
        <div className="error-banner" role="alert">
          <WarningIcon className="error-banner__icon" />
          <span>Failed to load timesheet. Check your connection.</span>
          <button type="button" className="btn btn--secondary" onClick={retry}>
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && timesheet && celebrating ? (
        <CelebrationScreen
          timesheet={timesheet}
          note={submittedNote}
          onViewSheet={() => setCelebrating(false)}
          onGoNext={() => {
            // Advance to next week, but never past the forward cap.
            const next = addWeeks(weekStartDate, 1);
            if (next <= nextWeekCap) goToWeek(next);
            setCelebrating(false);
          }}
        />
      ) : null}

      {!isLoading && !isError && timesheet && !celebrating && (
        <>
          <PeriodBar
            period={timesheet.period}
            status={uiStatus}
            onPrev={() => goToWeek(addWeeks(weekStartDate, -1))}
            onNext={() => goToWeek(addWeeks(weekStartDate, 1))}
            disableNext={disableNext}
            outstandingWeeks={outstandingWeeks}
            outstandingTotal={outstandingTotal}
            showPulse={showPulse}
            showPopover={showPopover}
            onSelectWeek={handleSelectOutstandingWeek}
          />

          {uiStatus === 'returned' && timesheet.returnComment && (
            <ReturnedBanner
              approverName={timesheet.returnComment.actorDisplayName}
              comment={timesheet.returnComment.comment}
            />
          )}

          {periodClosed && (
            <div className="ts-period-closed-banner" role="alert">
              <LockIcon />
              This reporting period is closed. Submissions and edits for this week are locked.
              Contact an administrator if you need to make a change.
            </div>
          )}

          {timesheet.lines.length === 0 ? (
            <EmptyState />
          ) : (
            <TimesheetGrid
              timesheet={timesheet}
              categories={categories}
              committedOverrides={committedOverrides}
              locallyRestricted={locallyRestricted}
              onCellReject={handleCellReject}
              onCellCommit={handleCellCommit}
              readOnly={(uiStatus !== 'open' && uiStatus !== 'returned') || periodClosed}
              cellRefs={cellRefs}
              lineIds={lineIds}
              onNoteClick={handleNoteClick}
              openNoteLineId={openNote?.lineId ?? null}
            />
          )}

          <ActionBar
            weekTotal={weekTotal}
            weeklyMinuteTarget={timesheet.weeklyMinuteTarget}
            saveState={saveState}
            status={uiStatus}
            onRequestSubmit={() => setSubmitOpen(true)}
            onRecall={handleRecall}
            isSubmitting={submitTimesheet.isPending}
            isRecalling={recallTimesheet.isPending}
            periodClosed={periodClosed}
          />

          {recap && (
            <SubmitDialog
              open={submitOpen}
              weekRange={weekRange}
              recap={recap}
              initialNote={timesheet.comment ?? ''}
              isSubmitting={submitTimesheet.isPending}
              onCancel={() => setSubmitOpen(false)}
              onConfirm={handleSubmit}
            />
          )}
        </>
      )}

      {openNote && (
        <NotePopover
          key={openNote.lineId}
          lineId={openNote.lineId}
          taskName={openNote.taskName}
          initialNote={openNote.note}
          readOnly={
            periodClosed ||
            (timesheet
              ? apiStatusToUi(timesheet.status) !== 'open' &&
                apiStatusToUi(timesheet.status) !== 'returned'
              : false)
          }
          anchorRect={openNote.anchorRect}
          onClose={handleNoteClose}
          onSave={handleNoteSave}
          isSaving={patchLineDescription.isPending}
        />
      )}

      <ToastRegion toasts={toasts} onDismiss={dismissToast} />
    </Layout>
  );
}
